import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ReactivateWithEndDateDialogComponent } from 'src/app/core/components/dialogs/reactivate-with-end-date-dialog/reactivate-with-end-date-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/core/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { SpinnerDialogComponent } from 'src/app/core/components/dialogs/spinner-dialog/spinner-dialog.component';
import { MaximumActiveCampaignsExceededException } from 'src/app/core/exceptions/maximum-active-campaigns-exceeded-exception';
import { NoActiveSubscriptionException } from 'src/app/core/exceptions/no-active-subscription-exception';
import { ValidationException } from 'src/app/core/exceptions/validation-exception';
import { CampaignModel } from 'src/app/models/campaign-model';
import { UpdateCampaignModel } from 'src/app/models/update-campaign-model';
import { SpinnerService } from 'src/app/services/spinner.service';
import { DeleteCampaignComponentStore } from 'src/app/stores/components/campaign-delete/delete-campaign.component-store';
import { CampaignDetailComponentStore } from 'src/app/stores/components/campaign-detail/campaign-detail.component-store';
import { UpdateCampaignComponentStore } from 'src/app/stores/components/campaign-update/update-campaign.component-store';
import { selectBusinessAccount } from 'src/app/stores/global/app.selectors';
import { ResponsiveBaseComponent } from 'src/app/core/components/responsive-base/responsive-base.component';

@Component({
    selector: 'app-campaign-page',
    templateUrl: './campaign-page.component.html',
    styleUrls: ['./campaign-page.component.scss'],
    standalone: false
})
export class CampaignPageComponent extends ResponsiveBaseComponent implements OnInit {
  public viewModel$ = this.campaignDetailComponentStore.viewModel$;
  public editMode: boolean = false;
  public companyId: string;

  private readonly ngUnsubscribeState = new Subject<void>();
  private loadingSpinnerDialogRef: MatDialogRef<SpinnerDialogComponent>;
  private campaign: CampaignModel;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly campaignDetailComponentStore: CampaignDetailComponentStore,
    private readonly dialog: MatDialog,
    private readonly deleteCampaignComponentStore: DeleteCampaignComponentStore,
    private readonly updateCampaignComponentStore: UpdateCampaignComponentStore,
    private readonly snackBar: MatSnackBar,
    private readonly spinnerService: SpinnerService,
    private readonly store: Store,
    responsive: BreakpointObserver,
  ) {
    super(responsive);
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    
    this.route.params.subscribe(params => {
      this.store.select(selectBusinessAccount).pipe(filter(result => Boolean(result))).subscribe(businessAccount => {
        const gettingStartedCompanyDetails: boolean = !businessAccount.company.name || businessAccount.company.name === '';
        const gettingStartedCompanyLogo: boolean = !businessAccount.company.logo;
        if (gettingStartedCompanyDetails || gettingStartedCompanyLogo) {
          this.router.navigate(['/getting-started']);
        }
        else {
          this.companyId = params.companyId;
          this.showSpinner();
          this.campaignDetailComponentStore.getCampaign({ companyId: params.companyId, campaignId: params.campaignId });
        }
      });
    });

    this.viewModel$.pipe(takeUntil(this.ngUnsubscribeState), filter(result => Boolean(result.loaded))).subscribe(viewModel => {
      this.campaign = viewModel.campaign;
    });

    this.deleteCampaignComponentStore.success$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((result) => {
      if (result) {
        this.resetComponentStores();
        this.snackBar.open("Stamp card archived successfully", "Dismiss");
        this.router.navigate(['/stamp-cards']);
      }
    });

    this.deleteCampaignComponentStore.errorMessage$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((errorMessage) => {
      if (errorMessage !== null && errorMessage !== undefined) {
        this.resetComponentStores();
        if (errorMessage instanceof ValidationException) {
          const validationViolationMessage = errorMessage.violations.map(v => v.message).join("\n");
          this.snackBar.open(validationViolationMessage, "Dismiss");
        }
        else {
          this.snackBar.open(errorMessage, "Dismiss");
        }
      }
    });

    this.updateCampaignComponentStore.success$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((result) => {
      if (result) {
        this.resetComponentStores();
        this.snackBar.open("Stamp card updated successfully", "Dismiss");
      }
    });

    this.updateCampaignComponentStore.errorMessage$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((errorMessage) => {
      if (errorMessage !== null && errorMessage !== undefined) {
        this.resetComponentStores();

        let displayErrorMessage = "Something went wrong.";
        if (errorMessage instanceof NoActiveSubscriptionException) {
          displayErrorMessage = 'No active subscription plan order. Please buy a subscription plan first.';
        }
        else if (errorMessage instanceof MaximumActiveCampaignsExceededException) {
          displayErrorMessage = 'You have reached the maximum amount of active stamp cards with your current subscription. If you want more active stamp cards, consider upgrading your subscription plan.';
        }
        if (errorMessage instanceof ValidationException) {
          const validationViolationMessage = errorMessage.violations.map(v => v.message).join("\n");
          this.snackBar.open(validationViolationMessage, "Dismiss");
        }
        else {
          this.snackBar.open(displayErrorMessage, "Dismiss");
        }
      }
    });

    this.updateCampaignComponentStore.campaign$.pipe(takeUntil(this.ngUnsubscribeState), filter(result => Boolean(result))).subscribe((updatedCampaign) => {
      this.campaignDetailComponentStore.setInitial();
      this.campaignDetailComponentStore.getCampaign({ companyId: updatedCampaign.company.id, campaignId: updatedCampaign.id });
    });

    this.campaignDetailComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });

    this.updateCampaignComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });

    this.deleteCampaignComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribeState.next();
    this.ngUnsubscribeState.complete();
  }

  public onDelete(event: any): void {
    this.resetComponentStores();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: "Are you sure?",
        message: "You want to archive this stamp card",
        data: this.campaign.name,
        confirm: "Archive"
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.showSpinner();
        this.deleteCampaignComponentStore.deleteCampaign({ companyId: this.campaign.company.id, campaignId: this.campaign.id });
      }
    });
  }

  public onActivate(event: any): void {
    this.resetComponentStores();
    this.showSpinner();

    this.updateCampaign(new Date(this.campaign.endDate));
  }

  public onReactivate(event: any): void {
    const dialogRef = this.dialog.open(ReactivateWithEndDateDialogComponent, {
      data: {
        title: "Reactivate Stamp Card",
        message: "Please select a new end date for",
        data: this.campaign.name,
        endDate: this.campaign.endDate,
        confirm: "Reactivate"
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.endDate) {
        this.resetComponentStores();
        this.showSpinner();

        this.updateCampaign(result.endDate);
      }
    });
  }

  public onEdit(event: any): void {
    this.editMode = true;
  }

  public onEditCancelPressed(event: any): void {
    this.editMode = false;
  }

  public onUpdateSuccess(event: any): void {
    this.editMode = false;
  }

  private resetComponentStores() {
    this.deleteCampaignComponentStore.setInitial();
    this.updateCampaignComponentStore.setInitial();
  }

  private showSpinner() {
    this.loadingSpinnerDialogRef = this.spinnerService.show();
  }

  private hideSpinner(loaded: boolean) {
    if (loaded && this.loadingSpinnerDialogRef !== null) {
      this.spinnerService.hide(this.loadingSpinnerDialogRef);
      this.loadingSpinnerDialogRef = null;
    }
  }

  private updateCampaign(campaignEndDate: Date): void {
    const updateCampaign = new UpdateCampaignModel();
    updateCampaign.name = this.campaign.name;
    updateCampaign.description = this.campaign.description;
    updateCampaign.startDate = new Date(this.campaign.startDate);
    updateCampaign.endDate = campaignEndDate;
    updateCampaign.campaignRewardName = this.campaign.campaignReward.name;
    updateCampaign.campaignRewardDescription = this.campaign.campaignReward.description;
    updateCampaign.voucherDuration = this.campaign.voucherDuration;
    updateCampaign.stampsObjective = this.campaign.stampsObjective;
    updateCampaign.recurrencePattern = this.campaign.recurrencePattern ? this.campaign.recurrencePattern : "";
    updateCampaign.campaignStatus = "ACTIVE";

    this.updateCampaignComponentStore.updateCampaign({ companyId: this.campaign.company.id, campaignId: this.campaign.id, updateCampaign: updateCampaign });
  }
}
